/**
 * AdditionalCostItemModal — port of V1 lib_editrecord.pl::additionalcost.
 *
 * Header + 6-row line-item grid. Invoice Total is the sum of line-item
 * totals; Out of Pocket = invoiceTotal − sum(rows where paystatus='PAY').
 * The whole form posts in one shot via the additional-cost-items endpoint.
 *
 * V1 has a complex ACL system and a few status workflows (HOLD → hold
 * type select, MANUAL TRANSFER read-only after export, etc.) that we
 * approximate rather than mirror exactly. Field-level edit gating is a
 * follow-up.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import DateTimePicker from '../DateTimePicker';
import Toggle from '../Toggle';
import PermissionsContainer, { useResolvedAccessLevel } from '../PermissionsContainer';
import { parseMdYDate } from '../../utils/dateUtils';
import { useAltHotkeys } from '../../hooks/useAltHotkeys';
import { useModalDrag } from '../../hooks/useModalDrag';
import ModalHeader from '../ui/ModalHeader';
import { WarningConfirmModal } from '../ui/WarningConfirmModal';
import { ClockIcon } from '@heroicons/react/24/outline';
import { HistoryDrawer } from '../HistoryDrawer';

/**
 * Component-permission identifiers for additional-cost-item write actions
 * (Add / Save / Delete). PermissionsContainer keys off these so admins
 * can configure access via the Permissions Mode cog overlay.
 */
export const COST_ITEM_PERMISSION_ID = 'pro-addl-cost-cost-item-write';
export const COST_ITEM_PERMISSION_NAME = 'Pro Addl Cost — Cost Item Add/Edit';
import {
  additionalCostItemsService,
  type AddlCostBulkColumn,
  type CostItem,
  type CostItemLineItem,
  type CostItemPatch,
  type CostItemReferenceData,
  type VendorOption,
} from '../../services/additionalCostItems.service';
import {
  isApproved,
  isExported,
  isIdentityLocked,
  isStatusFieldLocked,
  isLineApprovalLocked,
  filterPayStatuses,
  statusOptions,
  clearApprovalOnDowngrade,
  validateCostItem,
  lineRowCount,
} from './additionalCostItemRules';
import { useChangeAll } from '../../pages/pro/changeAll/ChangeAllContext';
import AddlCostChangeAllReviewModal from './AddlCostChangeAllReviewModal';

// The 8 bulk-editable Addl Cost fields (V1 updateall_add_*), with display labels.
const ADDL_COST_BULK_FIELDS: ReadonlyArray<readonly [AddlCostBulkColumn, string]> = [
  ['vendorcode', 'Vendor'],
  ['invoicenumber', 'Invoice #'],
  ['invoicedate', 'Invoice Date'],
  ['receiveddate', 'Received Date'],
  ['ponumber', 'PO #'],
  ['status', 'Status'],
  ['holdtyperecno', 'Hold Type'],
  ['checknumber', 'Check #'],
];

const HISTORY_FIELDS: Record<string, string> = {
  vendorcode: 'Vendor', invoicenumber: 'Invoice #', invoicedate: 'Invoice Date',
  receiveddate: 'Received Date', approvedby: 'Approved By', approveddatetime: 'Approved Date/Time',
  invoicetotal: 'Invoice Total', tmwrate: 'TMW Rate', costtype: 'Type', numdays: 'Days',
  reason: 'Reason', ponumber: 'PO #', dispute: 'Dispute', disputereason: 'Dispute Reason',
  comments: 'Comments', masexportdate: 'Sage Export Date', status: 'Status', holdtyperecno: 'Hold Type',
  adjustamount: 'Adjust Amount', checknumber: 'Check #', creditrequesteddatetime: 'Credit Requested',
  creditamountrequested: 'Credit Amount Requested', deleted: 'Deleted', createdby: 'Created By',
  createdatetime: 'Created Date/Time', qty: 'Quantity', rate: 'Rate', paystatus: 'Pay Status',
  payreason: 'Pay Reason', glcode: 'GL Code', personresponsible: 'Person Responsible',
  groupresponsible: 'Department', total: 'Total',
};
const HISTORY_TABLES = { additionalcost: 'Additional Cost' };

const formatMdYDate = (d: Date | null): string => {
  if (!d) return '';
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${m}/${day}/${d.getFullYear()}`;
};

const emptyLine = (): CostItemLineItem => ({
  recno: null,
  qty: 0,
  rate: 0,
  paystatus: '',
  payreason: '',
  glcode: '',
  personresponsible: '',
  groupresponsible: '',
  total: 0,
  deleted: 'N',
});

const padLines = (lines: CostItemLineItem[]): CostItemLineItem[] => {
  const target = lineRowCount(lines.length);
  const out = [...lines];
  while (out.length < target) out.push(emptyLine());
  return out.slice(0, target);
};

const blankForm = (pronumber: number): CostItem => ({
  recno: null,
  pronumber,
  vendorcode: 0,
  vendorName: '',
  invoicedate: '',
  invoicenumber: '',
  receiveddate: '',
  approvedby: '',
  approveddatetime: '',
  invoicetotal: 0,
  tmwrate: 0,
  costtype: '',
  numdays: 0,
  reason: '',
  ponumber: '',
  dispute: 'N',
  disputereason: '',
  comments: '',
  masexportdate: '',
  status: 'OPEN',
  holdtyperecno: 0,
  holdtypeName: '',
  adjustamount: 0,
  checknumber: '',
  creditrequesteddatetime: '',
  creditamountrequested: 0,
  deleted: 'N',
  createdby: '',
  createdatetime: '',
  lineItems: padLines([]),
});

interface Props {
  isOpen: boolean;
  onClose: () => void;
  pronumber: number;
  /** Recno of the row being edited. Null = new row. */
  recno: number | null;
  /** Called after a successful create / update / delete. May return a
   *  Promise; the modal awaits it before closing so the parent table is
   *  up-to-date the moment the modal disappears. */
  onSaved: () => void | Promise<void>;
}

const Label: React.FC<{ children: React.ReactNode; htmlFor?: string }> = ({ children, htmlFor }) => (
  <label
    htmlFor={htmlFor}
    className="block text-[0.625rem] font-semibold uppercase tracking-wider text-slate-500 dark:text-gray-400 mb-0.5"
  >
    {children}
  </label>
);

const inputClass =
  'w-full h-7 px-1.5 text-[0.75rem] rounded border border-slate-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-slate-900 dark:text-gray-100 ' +
  'focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20 disabled:bg-slate-100 dark:disabled:bg-gray-700 disabled:text-slate-500 dark:disabled:text-gray-400';

const readonlyClass =
  'w-full h-7 px-1.5 text-[0.75rem] rounded border border-slate-200 dark:border-gray-700 bg-slate-50 dark:bg-gray-900 text-slate-600 dark:text-gray-300 cursor-not-allowed';

const moneyFormat = (n: number) =>
  n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const AdditionalCostItemModal: React.FC<Props> = ({
  isOpen,
  onClose,
  pronumber,
  recno,
  onSaved,
}) => {
  const [refData, setRefData] = useState<CostItemReferenceData | null>(null);
  const [form, setForm] = useState<CostItem>(() => blankForm(pronumber));
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);

  useEffect(() => { setHistoryOpen(false); }, [isOpen, pronumber, recno]);

  // Change All: when the drawer is in change-all mode, the user can flag any of the 8
  // bulk fields to fan out to every cost record on the target PROs. Own local state
  // (this modal isn't in ChangeAllContext's field set); reads targetPros from context.
  const { enabled: changeAllEnabled, targetPros } = useChangeAll();
  const [bulkChecked, setBulkChecked] = useState<ReadonlySet<AddlCostBulkColumn>>(new Set());
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewFields, setReviewFields] = useState<Partial<Record<AddlCostBulkColumn, unknown>>>({});
  const toggleBulk = (col: AddlCostBulkColumn) =>
    setBulkChecked((prev) => {
      const next = new Set(prev);
      if (next.has(col)) next.delete(col); else next.add(col);
      return next;
    });

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setError(null);
    setLoading(true);
    (async () => {
      try {
        const refPromise = additionalCostItemsService.fetchReferenceData(pronumber);
        const rowPromise = recno != null
          ? additionalCostItemsService.fetch(pronumber, recno)
          : Promise.resolve(null);
        const [ref, row] = await Promise.all([refPromise, rowPromise]);
        if (cancelled) return;
        setRefData(ref);
        if (row) {
          setForm({ ...row, lineItems: padLines(row.lineItems) });
        } else {
          setForm(blankForm(pronumber));
        }
      } catch (err: unknown) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load cost item');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, pronumber, recno]);

  // V1 lib_editrecord.pl:4738 — edit-gating is per-field, not all-or-nothing.
  // Identity + amount fields (Vendor, Type, Invoice #/Date, Received Date, PO#,
  // line Qty/Rate, Deleted) lock once the item is approved OR exported, while
  // workflow fields (Status, Check #, Adjust Amt, Reason, Comments, line Pay
  // Status/GL/Dept) stay editable. See additionalCostItemRules.ts.
  const identityLocked = isIdentityLocked(form);
  const exported = isExported(form);
  const approved = isApproved(form);

  // Derived totals — recompute whenever any line-item changes.
  const invoiceTotal = useMemo(
    () => form.lineItems.reduce((acc, li) => acc + (Number(li.total) || 0), 0),
    [form.lineItems],
  );
  const outOfPocket = useMemo(
    () => form.lineItems
      .filter((li) => li.paystatus === 'PAY' && li.deleted !== 'Y')
      .reduce((acc, li) => acc + (Number(li.total) || 0), 0),
    [form.lineItems],
  );

  const field = <K extends keyof CostItem>(key: K, value: CostItem[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const updateLine = (idx: number, patch: Partial<CostItemLineItem>) => {
    setForm((prev) => {
      const next = [...prev.lineItems];
      const merged = { ...next[idx], ...patch };
      // Auto-compute total = qty * rate when either changes.
      if (patch.qty != null || patch.rate != null) {
        merged.total = Math.round((merged.qty || 0) * (merged.rate || 0) * 100) / 100;
      }
      next[idx] = merged;
      return { ...prev, lineItems: next };
    });
  };

  // V1 lib_editrecord.pl:5793 — sending a row back to OPEN/HOLD clears the
  // approver stamp + adjust amount, which also unlocks the identity fields.
  const handleStatusChange = (newStatus: string) => {
    setForm((prev) => {
      const next = { ...prev, status: newStatus };
      const cleared = clearApprovalOnDowngrade(newStatus);
      return cleared ? { ...next, ...cleared } : next;
    });
  };

  // View-level (1) callers get the whole modal read-only: the container only
  // hides at 0, so anything below Edit (2) must disable itself here.
  const saveAccess = useResolvedAccessLevel(COST_ITEM_PERMISSION_ID);
  const canWrite = saveAccess >= 2;

  const handleSave = async () => {
    if (!canWrite) return;
    // All field/line validations live in the pure rules module (V1 $saveclick
    // parity + the two kept GWI guards). See additionalCostItemRules.ts.
    const validationError = validateCostItem(form);
    if (validationError) {
      setError(validationError);
      return;
    }
    // Moving a row back to OPEN/HOLD clears the approver stamp + adjust amount
    // (V1 lib_editrecord.pl:5793,5869,5874). Persist the cleared values.
    const downgrade = clearApprovalOnDowngrade(form.status);
    const eff = downgrade ? { ...form, ...downgrade } : form;
    setSaving(true);
    setError(null);
    try {
      const patch: CostItemPatch = {
        vendorcode: eff.vendorcode,
        invoicedate: eff.invoicedate,
        invoicenumber: eff.invoicenumber,
        receiveddate: eff.receiveddate,
        approvedby: eff.approvedby,
        approveddatetime: eff.approveddatetime,
        invoicetotal: invoiceTotal,
        tmwrate: eff.tmwrate,
        costtype: eff.costtype,
        numdays: eff.numdays,
        reason: eff.reason,
        ponumber: eff.ponumber,
        dispute: eff.dispute,
        disputereason: eff.disputereason,
        comments: eff.comments,
        status: eff.status || 'OPEN',
        holdtyperecno: eff.status === 'HOLD' ? eff.holdtyperecno : 0,
        adjustamount: eff.adjustamount,
        checknumber: eff.checknumber,
        creditrequesteddatetime: eff.creditrequesteddatetime,
        creditamountrequested: eff.creditamountrequested,
        deleted: eff.deleted,
        lineItems: eff.lineItems,
      };
      if (recno == null) {
        await additionalCostItemsService.create(pronumber, patch);
      } else {
        await additionalCostItemsService.update(pronumber, recno, patch);
      }
      await onSaved();
      // Change All: the anchor record saved normally above; now fan the flagged fields
      // out to every cost record on the target PROs via the review modal (keep this
      // modal mounted underneath — the review overlays and closes both when done).
      if (changeAllEnabled && bulkChecked.size > 0) {
        const captured: Partial<Record<AddlCostBulkColumn, unknown>> = {};
        for (const col of bulkChecked) captured[col] = (eff as unknown as Record<string, unknown>)[col];
        setReviewFields(captured);
        setReviewOpen(true);
      } else {
        onClose();
      }
    } catch (err: unknown) {
      const detail =
        typeof err === 'object' && err && 'response' in err
          ? (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
          : undefined;
      setError(detail || (err instanceof Error ? err.message : 'Failed to save'));
    } finally {
      setSaving(false);
    }
  };

  // Delete asks first — in-app (WarningConfirmModal), not window.confirm, which
  // renders as a browser-chrome popup and blocks the page.
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const handleDelete = () => {
    if (recno == null || !canWrite) return;
    setConfirmDeleteOpen(true);
  };
  const performDelete = async () => {
    setConfirmDeleteOpen(false);
    if (recno == null || !canWrite) return;
    setSaving(true);
    setError(null);
    try {
      await additionalCostItemsService.delete(pronumber, recno);
      await onSaved();
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to delete');
    } finally {
      setSaving(false);
    }
  };

  // Alt+S / Alt+C, the same chords as the Pro footer behind us. Claiming the
  // layer is what keeps Alt+S from reaching the drawer's Save Changes; the
  // binding itself mirrors the Save button exactly — same permission gate as the
  // PermissionsContainer below, so a read-only user can't save via the keyboard.
  // BOTH chords go unbound while the Change All review is up: that modal renders
  // on top while this one stays mounted underneath, so an Alt+C there would close
  // the modal below the overlay the user is actually looking at. The layer stays
  // claimed in that state, so the chords are swallowed rather than falling through.
  const rootRef = useRef<HTMLDivElement | null>(null);
  useAltHotkeys(
    {
      // Also unbound while the delete confirm is up (it owns Alt+C then).
      S: isOpen && !saving && !loading && !reviewOpen && !historyOpen && !confirmDeleteOpen && saveAccess >= 2 ? handleSave : null,
      C: isOpen && !saving && !reviewOpen && !historyOpen && !confirmDeleteOpen ? onClose : null,
    },
    rootRef,
    { layer: isOpen },
  );

  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(isOpen);

  if (!isOpen) return null;

  return (
    <div
      ref={rootRef}
      role="dialog"
      aria-modal="true"
      aria-label={recno == null ? 'New Additional Cost Item' : 'Cost Item'}
      className="fixed inset-0 z-[100] overflow-y-auto"
      data-testid="additional-cost-item-modal"
    >
      {historyOpen && recno != null && (
        <HistoryDrawer
          key={`${pronumber}-${recno}`}
          isOpen
          onClose={() => setHistoryOpen(false)}
          endpoint={`/orders/${pronumber}/additional-cost-items/${recno}/history`}
          title={`Additional Cost #${recno} History — PRO ${pronumber}`}
          tableDisplayNames={HISTORY_TABLES}
          columnDisplayNames={HISTORY_FIELDS}
          otherIdLabel="Line"
        />
      )}
      <AddlCostChangeAllReviewModal
        isOpen={reviewOpen}
        pros={targetPros}
        fields={reviewFields}
        onApplied={onSaved}
        onClose={() => { setReviewOpen(false); onClose(); }}
      />
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 sm:p-0">
        <div className="fixed inset-0 z-[100] bg-gray-500 dark:bg-gray-900 opacity-75" onClick={onClose} />
        <div
          ref={panelRef}
          style={panelStyle}
          className="relative z-[101] inline-block bg-panel rounded-lg shadow-xl sm:my-8 max-w-5xl w-full text-left"
        >
          <ModalHeader
            title={recno == null ? 'New Additional Cost Item' : `Cost Item: ${form.costtype || ''}`}
            onClose={onClose}
            dragHandleProps={dragHandleProps}
            actions={recno != null && (
              <button
                type="button"
                onClick={() => setHistoryOpen(true)}
                className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium hover:bg-white/10"
              >
                <ClockIcon className="h-4 w-4" aria-hidden="true" />
                View History
              </button>
            )}
          />

          <div className="px-4 py-3 max-h-[80vh] overflow-y-auto">
            {/* View-level (1) callers get the whole form read-only: a disabled
                fieldset inert-ifies every native control and pointer-events-none
                stops the custom typeaheads/date pickers from opening. */}
            <fieldset
              disabled={!canWrite}
              className={`min-w-0 border-0 p-0 m-0${!canWrite ? ' pointer-events-none' : ''}`}
            >
            {(exported || approved) && (
              <div className="mb-2 px-2 py-1 text-[0.75rem] text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 rounded">
                {exported
                  ? 'Exported to Sage — vendor, type, invoice details and line amounts are locked; Adjust Amount and workflow fields remain editable.'
                  : 'Approved — vendor, type, invoice details and line amounts are locked; status and workflow fields remain editable.'}
              </div>
            )}
            {error && (
              <div className="mb-2 px-2 py-1 text-[0.75rem] text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded">
                {error}
              </div>
            )}
            {loading && (
              <div className="text-center text-[0.75rem] text-slate-500 dark:text-gray-400 py-4">Loading…</div>
            )}

            {changeAllEnabled && !loading && (
              <div
                data-testid="addl-cost-change-all-bar"
                className="mb-2 px-2.5 py-2 rounded border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20"
              >
                <div className="text-[0.6875rem] font-semibold uppercase tracking-wider text-amber-800 dark:text-amber-300 mb-1">
                  Change All — apply checked fields to every cost record on {targetPros.length} pro{targetPros.length === 1 ? '' : 's'}
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1">
                  {ADDL_COST_BULK_FIELDS.map(([col, lbl]) => (
                    <label key={col} className="inline-flex items-center gap-1 text-[0.75rem] text-slate-700 dark:text-gray-200 cursor-pointer">
                      <input
                        type="checkbox"
                        className="h-3.5 w-3.5"
                        checked={bulkChecked.has(col)}
                        onChange={() => toggleBulk(col)}
                      />
                      {lbl}
                    </label>
                  ))}
                </div>
                {bulkChecked.size > 0 && (
                  <div className="mt-1 text-[0.6875rem] text-amber-700 dark:text-amber-400">
                    On Save, the anchor record saves normally and the checked field{bulkChecked.size === 1 ? '' : 's'} fan out to the target pros.
                  </div>
                )}
              </div>
            )}

            {!loading && refData && (
              <div className="grid grid-cols-2 gap-4">
                {/* LEFT COLUMN */}
                <div className="space-y-3">
                  <VendorPicker
                    pronumber={pronumber}
                    vendorcode={form.vendorcode}
                    vendorName={form.vendorName}
                    disabled={identityLocked || saving}
                    onPick={(v) => {
                      field('vendorcode', v?.recno ?? 0);
                      field('vendorName', v?.name ?? '');
                    }}
                  />

                  <div>
                    <Label>Reason</Label>
                    <select
                      className={inputClass}
                      value={form.reason}
                      onChange={(e) => field('reason', e.target.value)}
                      disabled={saving}
                    >
                      <option value="">—</option>
                      {refData.reasons.map((r) => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <Label>Comments</Label>
                    <textarea
                      rows={5}
                      className={inputClass.replace('h-7', 'h-auto py-1')}
                      value={form.comments}
                      onChange={(e) => field('comments', e.target.value)}
                      disabled={saving}
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <Label>Status</Label>
                      <select
                        data-testid="ac-status"
                        className={inputClass}
                        value={form.status}
                        onChange={(e) => handleStatusChange(e.target.value)}
                        disabled={isStatusFieldLocked(form) || saving}
                      >
                        {statusOptions(refData.statuses, form.status).map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label>Check #</Label>
                      <input
                        className={inputClass}
                        value={form.checknumber}
                        onChange={(e) => field('checknumber', e.target.value)}
                        disabled={saving}
                      />
                    </div>
                    <div>
                      <Label>Deleted</Label>
                      <div className="flex items-center h-7 gap-2">
                        <Toggle
                          checked={form.deleted === 'Y'}
                          onChange={(c) => field('deleted', c ? 'Y' : 'N')}
                          size="sm"
                          ariaLabel="Deleted"
                          disabled={identityLocked || saving}
                        />
                        <span className="text-[0.75rem] text-slate-600 dark:text-gray-300">
                          {form.deleted === 'Y' ? 'Yes' : 'No'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {form.status === 'HOLD' && (
                    <div>
                      <Label>Hold Type</Label>
                      <select
                        className={inputClass}
                        value={form.holdtyperecno}
                        onChange={(e) => field('holdtyperecno', parseInt(e.target.value, 10) || 0)}
                        disabled={saving}
                      >
                        <option value={0}>— Select hold type —</option>
                        {refData.holdTypes.map((h) => (
                          <option key={h.recno} value={h.recno}>{h.type}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label>Credit Requested</Label>
                      <div className="flex items-center h-7 gap-2">
                        <Toggle
                          checked={!!form.creditrequesteddatetime}
                          onChange={(c) =>
                            field(
                              'creditrequesteddatetime',
                              c ? new Date().toISOString().replace('T', ' ').slice(0, 16).replace(/-/g, '/') : '',
                            )
                          }
                          size="sm"
                          ariaLabel="Credit Requested"
                          disabled={saving}
                        />
                        <span className="text-[0.6875rem] text-slate-500 dark:text-gray-400 truncate">
                          {form.creditrequesteddatetime || '—'}
                        </span>
                      </div>
                    </div>
                    <div>
                      <Label>Credit Amount Req</Label>
                      <input
                        type="number"
                        step="0.01"
                        className={inputClass}
                        value={form.creditamountrequested}
                        onChange={(e) =>
                          field('creditamountrequested', parseFloat(e.target.value) || 0)
                        }
                        disabled={saving}
                      />
                    </div>
                  </div>
                </div>

                {/* RIGHT COLUMN */}
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label>Type</Label>
                      <select
                        data-testid="ac-type"
                        className={inputClass}
                        value={form.costtype}
                        onChange={(e) => field('costtype', e.target.value)}
                        disabled={identityLocked || saving}
                      >
                        <option value="">— Select —</option>
                        {refData.costTypes.map((t) => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label>PO / Ref #</Label>
                      <input
                        className={inputClass}
                        value={form.ponumber}
                        onChange={(e) => field('ponumber', e.target.value)}
                        disabled={identityLocked || saving}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label>Invoice #</Label>
                      <input
                        className={inputClass}
                        maxLength={20}
                        value={form.invoicenumber}
                        onChange={(e) => field('invoicenumber', e.target.value)}
                        disabled={identityLocked || saving}
                      />
                    </div>
                    <div>
                      <Label>Invoice Date</Label>
                      <DateTimePicker
                        mode="date"
                        inputVariant="compact"
                        selectionType="single"
                        autoApply
                        openDirection="down"
                        autoFlip
                        usePortal
                        labelType="none"
                        placeholder="Invoice Date"
                        value={parseMdYDate(form.invoicedate)}
                        onChange={(v) =>
                          field('invoicedate', formatMdYDate(v as Date | null))
                        }
                        disabled={identityLocked || saving}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label>Received Date</Label>
                      <DateTimePicker
                        mode="date"
                        inputVariant="compact"
                        selectionType="single"
                        autoApply
                        openDirection="down"
                        autoFlip
                        usePortal
                        labelType="none"
                        placeholder="Received Date"
                        value={parseMdYDate(form.receiveddate)}
                        onChange={(v) =>
                          field('receiveddate', formatMdYDate(v as Date | null))
                        }
                        disabled={identityLocked || saving}
                      />
                    </div>
                    <div>
                      <Label>TMW Rate</Label>
                      <input
                        type="number"
                        step="0.01"
                        className={inputClass}
                        value={form.tmwrate}
                        onChange={(e) => field('tmwrate', parseFloat(e.target.value) || 0)}
                        disabled={identityLocked || saving}
                      />
                    </div>
                  </div>

                  {(form.costtype === 'PER DIEM' || form.costtype === 'STORAGE') && (
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label>Number of Days</Label>
                        <input
                          type="number"
                          min={0}
                          className={inputClass}
                          value={form.numdays}
                          onChange={(e) =>
                            field('numdays', parseInt(e.target.value, 10) || 0)
                          }
                          disabled={identityLocked || saving}
                        />
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label>Created By / At</Label>
                      <input
                        className={readonlyClass}
                        value={[form.createdby, form.createdatetime].filter(Boolean).join(' · ')}
                        readOnly
                      />
                    </div>
                    <div>
                      <Label>Approved By / At</Label>
                      <input
                        className={readonlyClass}
                        value={[form.approvedby, form.approveddatetime].filter(Boolean).join(' · ')}
                        readOnly
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label>Export Date</Label>
                      <input
                        className={readonlyClass}
                        value={form.masexportdate || ''}
                        readOnly
                      />
                    </div>
                    <div>
                      <Label>Invoice Total</Label>
                      <input
                        className={readonlyClass + ' text-right'}
                        value={moneyFormat(invoiceTotal)}
                        readOnly
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label>Out of Pocket</Label>
                      <input
                        className={readonlyClass + ' text-right'}
                        value={moneyFormat(outOfPocket)}
                        readOnly
                      />
                    </div>
                    <div>
                      <Label>Adjust Amount</Label>
                      <input
                        data-testid="ac-adjustamount"
                        type="number"
                        step="0.01"
                        className={inputClass}
                        value={form.adjustamount}
                        onChange={(e) => field('adjustamount', parseFloat(e.target.value) || 0)}
                        disabled={saving}
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Dispute</Label>
                    <div className="flex items-center h-7 gap-2">
                      <Toggle
                        checked={form.dispute === 'Y'}
                        onChange={(c) => field('dispute', c ? 'Y' : 'N')}
                        size="sm"
                        ariaLabel="Dispute"
                        disabled={saving}
                      />
                      <span className="text-[0.75rem] text-slate-600 dark:text-gray-300">
                        {form.dispute === 'Y' ? 'Yes' : 'No'}
                      </span>
                    </div>
                  </div>

                  {form.dispute === 'Y' && (
                    <div>
                      <Label>Dispute Reason</Label>
                      <select
                        className={inputClass}
                        value={form.disputereason}
                        onChange={(e) => field('disputereason', e.target.value)}
                        disabled={saving}
                      >
                        <option value="">—</option>
                        {refData.disputeReasons.map((r) => (
                          <option key={r} value={r}>{r}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                {/* LINE ITEMS — full width */}
                <div className="col-span-2 mt-2">
                  <div className="text-[0.6875rem] font-semibold uppercase tracking-wider text-slate-500 dark:text-gray-400 mb-1">
                    Line Items
                  </div>
                  <div className="border border-slate-200 dark:border-gray-700 rounded overflow-hidden">
                    <table className="w-full text-[0.75rem] table-fixed">
                      <thead className="bg-slate-50 dark:bg-gray-900">
                        <tr>
                          <th className="w-8 px-1 py-1 text-left text-[0.65625rem] font-semibold uppercase tracking-wider text-slate-500 dark:text-gray-400">Ln</th>
                          <th className="w-16 px-1 py-1 text-left text-[0.65625rem] font-semibold uppercase tracking-wider text-slate-500 dark:text-gray-400">Qty</th>
                          <th className="w-24 px-1 py-1 text-left text-[0.65625rem] font-semibold uppercase tracking-wider text-slate-500 dark:text-gray-400">Rate</th>
                          <th className="w-28 px-1 py-1 text-left text-[0.65625rem] font-semibold uppercase tracking-wider text-slate-500 dark:text-gray-400">Pay Status</th>
                          <th className="w-28 px-1 py-1 text-left text-[0.65625rem] font-semibold uppercase tracking-wider text-slate-500 dark:text-gray-400">GL Code</th>
                          <th className="px-1 py-1 text-left text-[0.65625rem] font-semibold uppercase tracking-wider text-slate-500 dark:text-gray-400">Department</th>
                          <th className="w-24 px-1 py-1 text-right text-[0.65625rem] font-semibold uppercase tracking-wider text-slate-500 dark:text-gray-400">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {form.lineItems.map((li, i) => (
                          <tr key={i} className="border-t border-slate-100 dark:border-gray-700">
                            <td className="px-1 py-0.5 text-slate-500 dark:text-gray-400">{i + 1}</td>
                            <td className="px-1 py-0.5">
                              <input
                                type="number"
                                step="1"
                                className={inputClass}
                                value={li.qty}
                                onChange={(e) =>
                                  updateLine(i, { qty: parseInt(e.target.value, 10) || 0 })
                                }
                                disabled={identityLocked || saving}
                              />
                            </td>
                            <td className="px-1 py-0.5">
                              <input
                                type="number"
                                step="0.01"
                                className={inputClass}
                                value={li.rate}
                                onChange={(e) =>
                                  updateLine(i, { rate: parseFloat(e.target.value) || 0 })
                                }
                                disabled={identityLocked || saving}
                              />
                            </td>
                            <td className="px-1 py-0.5">
                              <select
                                className={inputClass}
                                value={li.paystatus}
                                onChange={(e) => updateLine(i, { paystatus: e.target.value })}
                                disabled={isLineApprovalLocked(li.paystatus, form.status) || saving}
                              >
                                {filterPayStatuses(form.status, refData.payStatuses, li.paystatus).map((s) => (
                                  <option key={s || '_empty'} value={s}>{s || '—'}</option>
                                ))}
                              </select>
                            </td>
                            <td className="px-1 py-0.5">
                              {/* Open entry, as in V1 (lib_editrecord.pl:5468 <input type=text>).
                                  Most stored codes are not in the active cms.glcodes list, so a
                                  <select> could not display them. No <datalist> either: Chrome
                                  pops the full list on click and draws a ▼, so it reads as a
                                  dropdown, which is exactly what users asked us to remove. */}
                              <input
                                type="text"
                                data-testid={`ac-line-${i}-glcode`}
                                maxLength={45}
                                autoComplete="off"
                                className={inputClass}
                                value={li.glcode}
                                onChange={(e) => updateLine(i, { glcode: e.target.value })}
                                disabled={isLineApprovalLocked(li.paystatus, form.status) || saving}
                              />
                            </td>
                            <td className="px-1 py-0.5">
                              <select
                                className={inputClass}
                                value={li.groupresponsible}
                                onChange={(e) => updateLine(i, { groupresponsible: e.target.value })}
                                disabled={saving}
                              >
                                {refData.departments.map((d) => (
                                  <option key={d || '_empty'} value={d}>{d || '—'}</option>
                                ))}
                              </select>
                            </td>
                            <td className="px-1 py-0.5 text-right text-slate-700 dark:text-gray-300">
                              {moneyFormat(li.total)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
            </fieldset>
          </div>

          <div className="px-4 py-3 border-t border-slate-200 dark:border-gray-700 flex items-center justify-end gap-2">
            {recno != null && canWrite && (
              <PermissionsContainer
                componentId={COST_ITEM_PERMISSION_ID}
                componentName={COST_ITEM_PERMISSION_NAME}
                showCog={false}
                className="mr-auto"
              >
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={saving || identityLocked}
                  className="px-3 py-1.5 text-[0.75rem] font-medium text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded hover:bg-red-100 dark:hover:bg-red-900/40 disabled:opacity-50"
                >
                  Delete
                </button>
              </PermissionsContainer>
            )}
            <WarningConfirmModal
              open={confirmDeleteOpen}
              title="Delete this cost item?"
              message={<>This removes the <span className="font-semibold text-heading">{form.costtype || 'cost'}</span> item and its lines from PRO {pronumber}.</>}
              confirmLabel="Delete"
              cancelLabel="Cancel"
              confirmVariant="danger"
              onConfirm={performDelete}
              onCancel={() => setConfirmDeleteOpen(false)}
            />
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-3 py-1.5 text-[0.75rem] font-medium text-slate-700 dark:text-gray-300 bg-slate-50 dark:bg-gray-900 border border-slate-200 dark:border-gray-700 rounded hover:bg-slate-100 dark:hover:bg-gray-700 disabled:opacity-50"
            >
              {canWrite ? 'Cancel' : 'Close'}
            </button>
            {canWrite && (
              <PermissionsContainer
                componentId={COST_ITEM_PERMISSION_ID}
                componentName={COST_ITEM_PERMISSION_NAME}
                showCog={false}
              >
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving || loading}
                  className="px-3 py-1.5 text-[0.75rem] font-semibold text-white bg-blue-600 border border-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </PermissionsContainer>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Vendor typeahead
// ---------------------------------------------------------------------------

interface VendorPickerProps {
  pronumber: number;
  vendorcode: number;
  vendorName: string;
  disabled?: boolean;
  onPick: (v: VendorOption | null) => void;
}

const VendorPicker: React.FC<VendorPickerProps> = ({
  pronumber,
  vendorcode,
  vendorName,
  disabled = false,
  onPick,
}) => {
  const [query, setQuery] = useState(vendorName);
  const [items, setItems] = useState<VendorOption[]>([]);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setQuery(vendorName);
  }, [vendorName]);

  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim()) {
      setItems([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        const results = await additionalCostItemsService.searchVendors(pronumber, query);
        setItems(results);
        setActiveIdx(0);
      } catch {
        setItems([]);
      }
    }, 200);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, open, pronumber]);

  const pick = (v: VendorOption) => {
    setQuery(v.name);
    setOpen(false);
    onPick(v);
  };

  return (
    <div className="relative">
      <Label>Vendor</Label>
      <input
        className={inputClass}
        placeholder="Search vendor…"
        value={query}
        disabled={disabled}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          if (!e.target.value.trim()) onPick(null);
        }}
        onKeyDown={(e) => {
          if (!open || items.length === 0) return;
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIdx((i) => Math.min(i + 1, items.length - 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIdx((i) => Math.max(i - 1, 0));
          } else if (e.key === 'Enter') {
            e.preventDefault();
            pick(items[activeIdx]);
          }
        }}
      />
      {open && items.length > 0 && (
        <div className="absolute left-0 right-0 top-full mt-0.5 z-20 bg-panel border border-slate-300 dark:border-gray-600 rounded shadow-lg max-h-60 overflow-y-auto">
          {items.map((it, i) => (
            <div
              key={it.recno}
              onMouseDown={() => pick(it)}
              className={
                'px-2 py-1 text-[0.75rem] cursor-pointer ' +
                (i === activeIdx ? 'bg-blue-50 dark:bg-blue-900/30' : 'hover:bg-slate-50 dark:hover:bg-gray-700')
              }
            >
              <div className="font-semibold text-slate-900 dark:text-gray-100">{it.name}</div>
              <div className="text-slate-500 dark:text-gray-400 text-[0.65625rem]">
                {it.mas90code}
                {it.city || it.state ? ` · ${[it.city, it.state].filter(Boolean).join(', ')}` : ''}
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="mt-0.5 text-[0.65625rem] text-slate-500 dark:text-gray-400 h-4 truncate">
        {vendorcode ? `#${vendorcode}` : ''}
      </div>
    </div>
  );
};

export default AdditionalCostItemModal;
